/**
 * AccountModal — ProfitSpot
 * Subscription management + email preferences + Refer & Earn
 * Opened from the Sidebar user section
 */
import React, { useState, useEffect } from 'react';
import { X, Crown, Zap, CheckCircle, AlertTriangle, Mail, ToggleLeft, ToggleRight, Loader, ChevronRight, Gift, Copy, Users, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

const TIER_META = {
  free:      { label: 'Free',      color: '#6b7280', pools: 10 },
  pro:       { label: 'Pro',       color: '#00d4ff', pools: 500, price: '$29/mo' },
  elite:     { label: 'Elite',     color: '#ffa502', pools: '∞', price: '$79/mo' },
  // Legacy tier names → map to new tiers
  starter:   { label: 'Pro',       color: '#00d4ff', pools: 500, price: '$29/mo' },
  unlimited: { label: 'Elite',     color: '#ffa502', pools: '∞', price: '$79/mo' },
};

const FREE_FEATURES = ['10 yield pools', 'Basic APY data', 'IL Calculator', 'Simulator preview'];

export default function AccountModal({ onClose }) {
  const { user, token, setUser } = useAuth();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling]     = useState(false);
  const [cancelled, setCancelled]       = useState(false);
  const [togglingEmail, setTogglingEmail] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const tier        = user?.subscription?.tier || 'free';
  const tierMeta    = TIER_META[tier] || TIER_META.free;
  const isAdmin     = user?.role === 'admin';
  const isActivePaid = !['free'].includes(tier) && !isAdmin && user?.subscription?.is_active;
  const isTrial     = user?.subscription?.is_trial;
  // Show cancel for both paid & trial users (neither are "free")
  const canCancel   = isActivePaid && !isAdmin;
  const emailAlerts = user?.notification_settings?.email_alerts !== false;

  const appUrl = window.location.origin;
  const referralLink = referralCode ? `${appUrl}/explore?ref=${referralCode}` : null;

  // Load referral stats on open
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/referrals/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.referral_code) setReferralCode(d.referral_code);
        setReferralStats(d);
      })
      .catch((e) => console.warn('Failed to load referral stats:', e));
  }, [token]);

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await fetch(`${API_URL}/api/referrals/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.referral_code) {
        setReferralCode(data.referral_code);
        setReferralStats(prev => ({ ...(prev || {}), referral_code: data.referral_code }));
        toast.success('Referral code generated!');
      }
    } catch {
      toast.error('Failed to generate code');
    }
    setGeneratingCode(false);
  };

  const copyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied!');
    }
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/create-portal-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error(data.detail || 'Unable to open billing portal');
      }
    } catch {
      toast.error('Billing portal unavailable — contact support');
    }
    setOpeningPortal(false);
  };

  // ── Toggle email alerts ────────────────────────────────────────
  const toggleEmailAlerts = async () => {
    setTogglingEmail(true);
    const next = !emailAlerts;
    try {
      const settings = { ...(user?.notification_settings || {}), email_alerts: next };
      const res = await fetch(`${API_URL}/api/auth/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        setUser(prev => ({ ...prev, notification_settings: settings }));
        toast.success(next ? 'Email alerts enabled' : 'Email alerts disabled');
      } else {
        toast.error('Failed to update preference');
      }
    } catch {
      toast.error('Network error');
    }
    setTogglingEmail(false);
  };

  // ── Cancel subscription ────────────────────────────────────────
  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/cancel-subscription`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCancelled(true);
        setUser(prev => ({
          ...prev,
          subscription: { ...prev.subscription, tier: 'free', is_active: false },
        }));
        toast.success('Subscription cancelled successfully');
      } else {
        toast.error(data.detail || 'Cancellation failed');
        setShowCancelConfirm(false);
      }
    } catch {
      toast.error('Network error — please try again');
      setShowCancelConfirm(false);
    }
    setCancelling(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="account-modal"
    >
      <div className="bg-[#0f0f1a] border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h2 className="text-base font-bold text-white">Account Settings</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">

          {/* Account info */}
          <div className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Account</p>
            <p className="text-white font-medium text-sm">{user?.name || '—'}</p>
            <p className="text-gray-500 text-xs mt-0.5">{user?.email}</p>
          </div>

          {/* Subscription section */}
          <div className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Subscription</p>

            {cancelled ? (
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#00ff88] flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">Subscription cancelled</p>
                  <p className="text-gray-500 text-xs mt-0.5">You're now on the Free tier. Re-subscribe anytime.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: tierMeta.color }}>{tierMeta.label}</span>
                    {isTrial && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">TRIAL</span>
                    )}
                    {isAdmin && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#00ff88]/20 text-[#00ff88] rounded font-bold">ADMIN</span>
                    )}
                  </div>
                  {tierMeta.price && !isAdmin && (
                    <span className="text-xs text-gray-500">{tierMeta.price}</span>
                  )}
                </div>

                {isTrial && user?.subscription?.trial_days_remaining !== undefined && (
                  <div className="mb-3 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                    <p className="text-xs text-amber-400">
                      {user.subscription.trial_days_remaining} days remaining in your free trial
                    </p>
                  </div>
                )}

                {/* Free tier — upgrade CTA */}
                {tier === 'free' && !isAdmin && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 leading-relaxed">
                      You're on the free plan (25 pools, no alerts). Upgrade to unlock the full platform.
                    </p>
                    <a
                      href="/#pricing"
                      onClick={onClose}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#00ff88]/10 border border-[#00ff88]/20 text-[#00ff88] rounded-lg text-sm font-medium hover:bg-[#00ff88]/20 transition-colors"
                      data-testid="upgrade-from-settings-btn"
                    >
                      <Crown className="w-4 h-4" /> View Plans &amp; Upgrade
                    </a>
                  </div>
                )}

                {/* Admin — no billing */}
                {isAdmin && (
                  <p className="text-xs text-gray-600">Admin accounts are not billed — subscription management not applicable.</p>
                )}

                {/* Paid / Trial — Stripe portal + cancel */}
                {canCancel && (
                  <div className="space-y-2">
                    <button
                      onClick={handleOpenPortal}
                      disabled={openingPortal}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-800/50 border border-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-800 transition-colors disabled:opacity-50"
                      data-testid="manage-billing-btn"
                    >
                      <span className="flex items-center gap-2">
                        {openingPortal ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Crown className="w-3.5 h-3.5" />}
                        Manage Billing &amp; Invoices
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </button>

                    {!showCancelConfirm && (
                      <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-red-500/5 border border-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/10 transition-colors"
                        data-testid="cancel-subscription-btn"
                      >
                        <span>{isTrial ? 'Cancel Free Trial' : 'Cancel Subscription'}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* Confirmation dialog */}
                {showCancelConfirm && !cancelled && (
                  <div className="mt-2 bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-white mb-1">Are you sure?</p>
                        <p className="text-xs text-gray-400 leading-relaxed">
                          {isTrial
                            ? 'Cancelling your trial returns you to Free tier immediately — '
                            : `Cancelling your ${tierMeta.label} plan returns you to Free tier immediately — `}
                          you'll keep only {FREE_FEATURES.join(', ')}. You can re-subscribe anytime.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setShowCancelConfirm(false)}
                        disabled={cancelling}
                        className="py-2 text-sm text-gray-400 border border-gray-700 rounded-lg hover:border-gray-500 transition-colors"
                      >
                        Keep Plan
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="py-2 text-sm font-bold text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="confirm-cancel-btn"
                      >
                        {cancelling ? <Loader className="w-4 h-4 animate-spin" /> : 'Yes, Cancel'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Email preferences */}
          <div className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Email Preferences</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm text-white">Yield Alert Emails</p>
                  <p className="text-xs text-gray-600">Smart alerts for Elite subscribers</p>
                </div>
              </div>
              <button
                onClick={toggleEmailAlerts}
                disabled={togglingEmail}
                className="flex-shrink-0 ml-3 disabled:opacity-50"
                data-testid="email-alerts-toggle"
              >
                {togglingEmail ? (
                  <Loader className="w-6 h-6 text-gray-500 animate-spin" />
                ) : emailAlerts ? (
                  <ToggleRight className="w-8 h-8 text-[#00ff88]" />
                ) : (
                  <ToggleLeft className="w-8 h-8 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Refer & Earn */}
          <div className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800" data-testid="refer-earn-section">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-4 h-4 text-[#00ff88]" />
              <p className="text-xs text-gray-500 uppercase tracking-wider">Refer &amp; Earn</p>
            </div>
            <p className="text-xs text-gray-400 mb-3 leading-relaxed">
              Share your referral link. When a friend converts from trial to paid, you both earn <span className="text-[#00ff88] font-bold">1 free month</span>.
            </p>

            {referralCode ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 font-mono text-xs text-[#00ff88] truncate">
                    {referralLink}
                  </div>
                  <button
                    onClick={copyLink}
                    className="flex-shrink-0 p-2 bg-[#00ff88]/10 border border-[#00ff88]/20 text-[#00ff88] rounded-lg hover:bg-[#00ff88]/20 transition-colors"
                    data-testid="copy-referral-link-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                {referralStats && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div className="bg-gray-900/50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-white">{referralStats.total_referrals ?? 0}</p>
                      <p className="text-[10px] text-gray-600">Referred</p>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-[#00ff88]">{referralStats.converted ?? 0}</p>
                      <p className="text-[10px] text-gray-600">Converted</p>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-[#ffa502]">{referralStats.free_months_earned ?? 0}</p>
                      <p className="text-[10px] text-gray-600">Free Mo.</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleGenerateCode}
                disabled={generatingCode}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#00ff88]/10 border border-[#00ff88]/20 text-[#00ff88] text-sm font-medium rounded-lg hover:bg-[#00ff88]/20 transition-colors disabled:opacity-50"
                data-testid="generate-referral-btn"
              >
                {generatingCode ? <Loader className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {generatingCode ? 'Generating...' : 'Get My Referral Link'}
              </button>
            )}
          </div>

          {/* Legal links */}
          <div className="flex items-center justify-center gap-4 pt-1">
            <a href="/terms"   onClick={onClose} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Terms</a>
            <span className="text-gray-800">·</span>
            <a href="/privacy" onClick={onClose} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Privacy</a>
            <span className="text-gray-800">·</span>
            <a href="/unsubscribe" onClick={onClose} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Unsubscribe</a>
          </div>
        </div>
      </div>
    </div>
  );
}
